import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InteractionType } from "../../models/interactions/Interaction";
import { RootState } from "../Store";

export interface SearchResultsState {
  query: string;
  searchResultsObjects: SearchResultData[];
  selectedResultId: string;
  activeFilter: string;
}

export interface SearchResultData {
  objectId: string;
  matchingText: string;
  filterType: string;
  takeName?: string;
  interactionId?: string;
  interactionType?: InteractionType | null;
  profilePicture?: string;
}

const initialState: SearchResultsState = {
  query: "",
  searchResultsObjects: [],
  selectedResultId: "",
  activeFilter: "All",
};

export const searchResultsSlice = createSlice({
  name: "searchResults",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setSelectedResultId: (state, action: PayloadAction<string>) => {
      state.selectedResultId = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<SearchResultData[]>) => {
      state.searchResultsObjects = action.payload;
    },
    setActiveFilter: (state, action: PayloadAction<string>) => {
      state.activeFilter = action.payload;
    },
  },
});

// reducers
export const {
  setQuery,
  setSearchResults,
  setSelectedResultId,
  setActiveFilter,
} = searchResultsSlice.actions;

const selectSearchResultsState = (state: RootState) => state.searchResults;
const selectSearchResultsObjects = (state: RootState) =>
  selectSearchResultsState(state).searchResultsObjects;

export const selectQuery = (state: RootState) => state.searchResults.query;
export const selectSelectedResultId = (state: RootState) =>
  state.searchResults.selectedResultId;
export const selectActiveFilter = (state: RootState) =>
  state.searchResults.activeFilter;
export const selectFilteredSearchResults = createSelector(
  [selectSearchResultsObjects, selectActiveFilter],
  (searchResultsObjects, activeFilter) => {
    if (activeFilter === "All") {
      return searchResultsObjects;
    }

    return searchResultsObjects.filter(
      (element) => activeFilter === element.filterType
    );
  }
);

export const selectAllFilters = createSelector(
  [selectSearchResultsObjects],
  (searchResultsObjects) => {
    const filterPairs: Record<string, number> = {};

    searchResultsObjects.forEach((element) => {
      const filterType = element.filterType ?? "";
      filterPairs[filterType] = (filterPairs[filterType] || 0) + 1;
    });

    return filterPairs;
  }
);

export default searchResultsSlice.reducer;
